import {Icon} from '@wandb/weave/components/Icon';
import {LightboxImage} from '@wandb/weave/components/PagePanelComponents/LightboxImage';
import LRUCache from 'lru-cache';
import React, {useContext, useEffect, useMemo, useState} from 'react';
import styled, {css} from 'styled-components';

import {TargetBlank} from '../../../../../../common/util/links';
import {useEntityProject} from '../../context';
import {MiniAudioViewer} from '../../typeViews/Content/AudioView';
import {downloadFromUrl} from '../../typeViews/Content/Shared';
import {CustomWeaveTypeProjectContext} from '../../typeViews/CustomWeaveTypeProjectContext';
import {useGetTraceServerClientContext} from '../wfReactInterface/traceServerClientContext';

// Client-side gate only; the trace server owns the canonical set of resolvable
// schemes. A miss here is safe -- it falls back to the plain string view.
const REFERENCE_BUCKET_URI_SCHEMES = ['s3://', 'gs://', 'gcs://', 'az://'];
const IMAGE_EXTENSIONS = [
  '.png',
  '.jpg',
  '.jpeg',
  '.gif',
  '.webp',
  '.bmp',
  '.svg',
];
const VIDEO_EXTENSIONS = ['.mp4', '.webm', '.mov', '.m4v', '.ogg'];
const AUDIO_EXTENSIONS = ['.mp3', '.wav', '.m4a', '.flac', '.aac', '.opus'];
// Fixed media height. The ObjectViewer row is 'auto', so giving the media a
// fixed height (rather than width-relative) keeps the row's measured height
// stable and avoids the ResizeObserver oscillation width-relative media causes.
const MEDIA_ROW_HEIGHT = 240;
// The media element (and the loading placeholder) height; the auto row adds
// vertical padding around it to reach MEDIA_ROW_HEIGHT.
const MEDIA_CONTENT_HEIGHT = MEDIA_ROW_HEIGHT - 16;
// The audio waveform is a compact bar rather than a fill-the-box element, so it
// reserves a much shorter placeholder than image/video.
const AUDIO_CONTENT_HEIGHT = 56;
const AUDIO_WAVEFORM_HEIGHT = 24;

type ValueViewReferenceUriProps = {
  uri: string;
  fallback: React.ReactNode;
  renderMedia?: boolean;
};

/**
 * Renders a reference-bucket URI by resolving it to a short-lived signed URL
 * via the trace server. It renders an inline image when `renderMedia` is true
 * and the URI is image-like; otherwise it renders a download link. Renders the
 * caller-supplied fallback while loading or when the URI is not resolvable.
 */
export const ValueViewReferenceUri = ({
  uri,
  fallback,
  renderMedia = true,
}: ValueViewReferenceUriProps) => {
  const projectContext = useContext(CustomWeaveTypeProjectContext);
  const {entity, project} = useEntityProject();
  const getTsClient = useGetTraceServerClientContext();
  const projectId =
    projectContext == null
      ? `${entity}/${project}`
      : `${projectContext.entity}/${projectContext.project}`;
  const hasProject =
    projectContext == null
      ? entity !== '' && project !== ''
      : projectContext.entity !== '' && projectContext.project !== '';
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [mediaFailed, setMediaFailed] = useState(false);

  useEffect(() => {
    if (!hasProject) {
      setFailed(true);
      return;
    }
    let active = true;
    setFailed(false);
    setMediaFailed(false);
    setDownloadUrl(null);
    resolveReferenceUrl(getTsClient, projectId, uri)
      .then(url => {
        if (!active) {
          return;
        }
        if (url) {
          setDownloadUrl(url);
        } else {
          setFailed(true);
        }
      })
      .catch(() => {
        if (active) {
          setFailed(true);
        }
      });
    return () => {
      active = false;
    };
  }, [getTsClient, hasProject, projectId, uri]);

  if (failed) {
    return <>{fallback}</>;
  }
  if (downloadUrl == null) {
    // Reserve the media height while resolving so the auto row does not jump
    // when the signed URL lands; non-media URIs stay single-line.
    if (renderMedia && (isImageUri(uri) || isVideoUri(uri))) {
      return <MediaPlaceholder />;
    }
    if (renderMedia && isAudioUri(uri)) {
      return <AudioPlaceholder />;
    }
    return <>{fallback}</>;
  }

  // A reference whose object 404s falls through to the download link rather than
  // a stuck spinner or broken player.
  if (renderMedia && !mediaFailed) {
    if (isImageUri(uri)) {
      return (
        <MediaImage
          src={downloadUrl}
          alt={uri}
          onError={() => setMediaFailed(true)}
        />
      );
    }
    if (isVideoUri(uri)) {
      return (
        <MediaVideo
          src={downloadUrl}
          controls
          onError={() => setMediaFailed(true)}
        />
      );
    }
    if (isAudioUri(uri)) {
      return (
        <MiniAudioViewer
          audioSrc={downloadUrl}
          height={AUDIO_WAVEFORM_HEIGHT}
          downloadFile={() =>
            downloadFromUrl(downloadUrl, filenameFromUri(uri))
          }
          onError={() => setMediaFailed(true)}
        />
      );
    }
  }

  return (
    <Link>
      <Icon name="download" width={14} height={14} />
      <TargetBlank href={downloadUrl}>{uri}</TargetBlank>
    </Link>
  );
};

type MaybeReferenceMediaProps = {
  value: string;
  children: React.ReactNode;
};

type MaybeReferenceUriProps = MaybeReferenceMediaProps & {
  renderMedia?: boolean;
};

/**
 * Renders a reference-bucket URI through the shared presign flow; otherwise
 * renders the caller-supplied children. By default this emits a compact link,
 * which keeps table rows at their normal height.
 */
export const MaybeReferenceUri = ({
  value,
  children,
  renderMedia = false,
}: MaybeReferenceUriProps) => {
  if (!isReferenceBucketUri(value)) {
    return <>{children}</>;
  }
  return (
    <ValueViewReferenceUri
      uri={value}
      fallback={children}
      renderMedia={renderMedia}
    />
  );
};

export const MaybeReferenceMedia = ({
  value,
  children,
}: MaybeReferenceMediaProps) => {
  return (
    <MaybeReferenceUri value={value} renderMedia>
      {children}
    </MaybeReferenceUri>
  );
};

/**
 * Renders any reference-bucket URIs embedded in free text as a media strip.
 * Renders nothing when the text carries no reference URI (e.g. a user message
 * "render s3://...").
 */
export const ReferenceMediaInText = ({text}: {text: string}) => {
  const uris = useMemo(() => extractReferenceBucketUris(text), [text]);
  if (uris.length === 0) {
    return null;
  }
  return (
    <MediaStrip>
      {uris.map(uri => (
        <ValueViewReferenceUri key={uri} uri={uri} fallback={null} />
      ))}
    </MediaStrip>
  );
};

export const isReferenceBucketUri = (value: string): boolean =>
  REFERENCE_BUCKET_URI_SCHEMES.some(scheme => value.startsWith(scheme));

// Reference URIs sitting inside prose are missed by isReferenceBucketUri, which
// only matches whole values. Pull out each scheme-prefixed token, trimming
// trailing punctuation the URI picked up from the surrounding sentence.
const REFERENCE_URI_IN_TEXT = new RegExp(
  `(?:${REFERENCE_BUCKET_URI_SCHEMES.join('|')})\\S+`,
  'g'
);
const TRAILING_PUNCTUATION = /[.,;:!?)\]}>'"]+$/;

export const extractReferenceBucketUris = (text: string): string[] => {
  const matches = text.match(REFERENCE_URI_IN_TEXT) ?? [];
  return Array.from(
    new Set(matches.map(match => match.replace(TRAILING_PUNCTUATION, '')))
  );
};

const hasExtension = (uri: string, extensions: string[]): boolean => {
  const path = uri.split('?')[0].toLowerCase();
  return extensions.some(ext => path.endsWith(ext));
};

const isImageUri = (uri: string): boolean =>
  hasExtension(uri, IMAGE_EXTENSIONS);

const isVideoUri = (uri: string): boolean =>
  hasExtension(uri, VIDEO_EXTENSIONS);

export const isAudioUri = (uri: string): boolean =>
  hasExtension(uri, AUDIO_EXTENSIONS);

const filenameFromUri = (uri: string): string => {
  const path = uri.split('?')[0];
  return path.substring(path.lastIndexOf('/') + 1) || uri;
};

type GetTsClient = ReturnType<typeof useGetTraceServerClientContext>;
type CachedUrl = {url: string | null; expiresAtMs: number};

// Re-sign just before the signed URL expires.
const RESOLVE_REFRESH_MARGIN_MS = 60_000;
const FALLBACK_TTL_MS = 60 * 60 * 1000;
// Cache an unresolvable URI briefly so a bogus reference (common once URIs are
// pulled out of free prose) does not re-sign on every remount, while a bucket
// that is bound moments later still recovers quickly.
const NEGATIVE_TTL_MS = 60_000;
const RESOLVED_URL_CACHE_MAX = 500;
const resolvedUrlCache = new LRUCache<string, CachedUrl>({
  max: RESOLVED_URL_CACHE_MAX,
});

// Caches resolved URLs by project + uri so a virtualized grid scrolling media
// cells in and out does not re-sign each time. Concurrent resolves collapse in
// the trace-server client, which dedupes identical in-flight requests.
const resolveReferenceUrl = (
  getTsClient: GetTsClient,
  projectId: string,
  uri: string
): Promise<string | null> => {
  const key = `${projectId}::${uri}`;
  const cached = resolvedUrlCache.get(key);
  if (cached && isCacheFresh(cached)) {
    return Promise.resolve(cached.url);
  }
  return getTsClient()
    .filesResolveUrl({project_id: projectId, uris: [uri]})
    .then(res => {
      const url = res.download_urls[0] ?? null;
      cacheResolvedUrl(key, url, res.expires_at);
      return url;
    })
    .catch(() => {
      cacheResolvedUrl(key, null);
      return null;
    });
};

// Positive entries re-sign once inside the refresh margin; negative entries
// (url == null) short-circuit for their whole TTL.
const isCacheFresh = (cached: CachedUrl): boolean =>
  cached.url == null
    ? cached.expiresAtMs > Date.now()
    : cached.expiresAtMs - Date.now() > RESOLVE_REFRESH_MARGIN_MS;

const cacheResolvedUrl = (
  key: string,
  url: string | null,
  expiresAt?: string
): void => {
  if (url == null) {
    resolvedUrlCache.set(key, {
      url: null,
      expiresAtMs: Date.now() + NEGATIVE_TTL_MS,
    });
    return;
  }
  const expiresAtMs = expiresAt ? Date.parse(expiresAt) : NaN;
  resolvedUrlCache.set(key, {
    url,
    expiresAtMs: Number.isNaN(expiresAtMs)
      ? Date.now() + FALLBACK_TTL_MS
      : expiresAtMs,
  });
};

const Link = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;
Link.displayName = 'S.Link';

const MediaStrip = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;
MediaStrip.displayName = 'S.MediaStrip';

// Fills the fixed media row, scaling to fit without distortion or clipping.
const fillMedia = css`
  display: block;
  height: ${MEDIA_CONTENT_HEIGHT}px;
  max-width: 100%;
  object-fit: contain;
  object-position: left center;
`;

const MediaImage = styled(LightboxImage)`
  ${fillMedia}
`;
MediaImage.displayName = 'S.MediaImage';

const MediaVideo = styled.video`
  ${fillMedia}
`;
MediaVideo.displayName = 'S.MediaVideo';

// Holds the media height while the signed URL resolves so the auto row settles
// at its final height on first paint instead of growing when media loads.
const MediaPlaceholder = styled.div`
  height: ${MEDIA_CONTENT_HEIGHT}px;
`;
MediaPlaceholder.displayName = 'S.MediaPlaceholder';

const AudioPlaceholder = styled.div`
  height: ${AUDIO_CONTENT_HEIGHT}px;
`;
AudioPlaceholder.displayName = 'S.AudioPlaceholder';
